/**
 * Shared components for PreviewPanel
 *
 * Common UI elements used by both table and linked table previews.
 */

import { useState } from 'react';
import {
  Table,
  Link2,
  Check,
  Download,
  ExternalLink,
  FileJson,
  FileSpreadsheet,
  FileCode,
  FileText,
  Database,
  KeyRound,
} from 'lucide-react';
import {
  Button,
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  VersionCombobox,
  type VersionOption,
} from '@loader/ui';
import type { WorkspaceItemType, TableStatus, LinkedTableStatus } from '@loader/shared-types';

import { useToastStore } from '@/store/useToasts';
import { generatePublicApiUrl, generateODataUrl, generateLinkedTableODataUrl } from '@/features/links/utils/urlGenerator';
import { usePublicApiKey } from '@/features/links/hooks/usePublicApiKey';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function formatRowCount(count: number): string {
  if (count < 1000) return count.toLocaleString();
  if (count < 1_000_000) return `${(count / 1000).toFixed(1)}k`;
  return `${(count / 1_000_000).toFixed(1)}M`;
}

// ============================================================================
// ITEM ICON
// ============================================================================

interface ItemIconProps {
  type: WorkspaceItemType;
  className?: string;
}

export function ItemIcon({ type, className }: ItemIconProps) {
  switch (type) {
    case 'table':
      return <Table className={cn('text-blue-500', className)} />;
    case 'linked_table':
      return <Link2 className={cn('text-purple-500', className)} />;
    default:
      return <Table className={className} />;
  }
}


// ============================================================================
// STATUS SELECTOR
// ============================================================================

interface StatusSelectorProps {
  status: TableStatus | LinkedTableStatus;
  onStatusChange?: (status: string) => void;
  isPending?: boolean;
  /** Whether user can edit - viewers see read-only text */
  canEdit?: boolean;
}

export function StatusSelector({ status, onStatusChange, isPending, canEdit = true }: StatusSelectorProps) {
  // For viewers, render as plain text with same visual format
  if (!canEdit) {
    return (
      <span className="flex items-center gap-2 px-4 text-sm text-muted-foreground">
        <span
          className={cn(
            'size-2 rounded-full',
            status === 'active' ? 'bg-green-500' : 'bg-gray-400'
          )}
        />
        <span className="capitalize">{status}</span>
      </span>
    );
  }

  return (
    <Select
      value={status}
      onValueChange={onStatusChange}
      disabled={isPending}
    >
      <SelectTrigger className="h-5 w-auto border-0 p-0 shadow-none text-sm text-muted-foreground">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="active">
          <span className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-green-500" />
            Active
          </span>
        </SelectItem>
        <SelectItem value="disabled">
          <span className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-gray-400" />
            Disabled
          </span>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

// ============================================================================
// LINKS SECTION (Download + Copy URLs)
// ============================================================================

interface LinksSectionProps {
  itemId: string;
  itemType: WorkspaceItemType;
  /** For tables: single version selection */
  selectedVersion?: string;
  /** For linked tables: version selections per table alias */
  versionSelections?: Record<string, string>;
  /** For linked tables: variable parameter overrides */
  variableParams?: Record<string, string>;
}

export function LinksSection({
  itemId,
  itemType,
  selectedVersion = 'current',
  versionSelections = {},
  variableParams = {},
}: LinksSectionProps) {
  const [copied, setCopied] = useState<string | null>(null);
  const { success } = useToastStore();
  const { publicKey } = usePublicApiKey();

  const resourceType = itemType === 'table' ? 'table' : 'linkedTable';
  const isTable = itemType === 'table';
  const isLinkedTable = itemType === 'linked_table';

  // Generate URLs for different formats
  const jsonUrl = generatePublicApiUrl(itemId, resourceType, 'json', selectedVersion, publicKey, versionSelections, variableParams);
  const csvUrl = generatePublicApiUrl(itemId, resourceType, 'csv', selectedVersion, publicKey, versionSelections, variableParams);
  const xlsxUrl = generatePublicApiUrl(itemId, resourceType, 'xlsx', selectedVersion, publicKey, versionSelections, variableParams);
  const xmlUrl = generatePublicApiUrl(itemId, resourceType, 'xml', selectedVersion, publicKey, versionSelections, variableParams);

  // OData URL - per-table endpoint for tables, linked table endpoint with version config for linked tables
  // No API key in URL - Power BI/Excel should use Basic Auth (email + API key)
  const odataUrl = isTable
    ? generateODataUrl(itemId, selectedVersion)
    : isLinkedTable
      ? generateLinkedTableODataUrl(itemId, versionSelections, variableParams)
      : null;

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard API failed (permissions denied, insecure context, etc.)
      // Show the URL in a prompt as fallback
      window.prompt(`Copy ${label}:`, text);
    }
  };

  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <>
      {/* Spreadsheet */}
      <div>
        <label className="text-muted-foreground mb-1.5 block text-xs font-medium uppercase">
          Spreadsheet
        </label>
        <div className="space-y-2">
          {/* Excel row */}
          <div className="flex items-center gap-2">
            <div className="flex flex-1 items-center gap-2 text-sm">
              <FileSpreadsheet className="size-4 text-green-600" />
              <span>Excel</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={() => handleDownload(xlsxUrl)}
            >
              <Download className="size-3.5" />
              File
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={() => handleCopy(xlsxUrl, 'Excel URL')}
            >
              {copied === 'Excel URL' ? (
                <Check className="size-3.5 text-green-600" />
              ) : (
                <Link2 className="size-3.5" />
              )}
              Link
            </Button>
          </div>
          {/* CSV row */}
          <div className="flex items-center gap-2">
            <div className="flex flex-1 items-center gap-2 text-sm">
              <FileText className="size-4 text-blue-600" />
              <span>CSV</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={() => handleDownload(csvUrl)}
            >
              <Download className="size-3.5" />
              File
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={() => handleCopy(csvUrl, 'CSV URL')}
            >
              {copied === 'CSV URL' ? (
                <Check className="size-3.5 text-green-600" />
              ) : (
                <Link2 className="size-3.5" />
              )}
              Link
            </Button>
          </div>
        </div>
      </div>

      {/* Data */}
      <div>
        <label className="text-muted-foreground mb-1.5 block text-xs font-medium uppercase">
          Data
        </label>
        <div className="space-y-2">
          {/* JSON row */}
          <div className="flex items-center gap-2">
            <div className="flex flex-1 items-center gap-2 text-sm">
              <FileJson className="size-4 text-yellow-600" />
              <span>JSON</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={() => handleDownload(jsonUrl)}
            >
              <ExternalLink className="size-3.5" />
              Open
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={() => handleCopy(jsonUrl, 'JSON URL')}
            >
              {copied === 'JSON URL' ? (
                <Check className="size-3.5 text-green-600" />
              ) : (
                <Link2 className="size-3.5" />
              )}
              Link
            </Button>
          </div>
          {/* XML row */}
          <div className="flex items-center gap-2">
            <div className="flex flex-1 items-center gap-2 text-sm">
              <FileCode className="size-4 text-orange-600" />
              <span>XML</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={() => handleDownload(xmlUrl)}
            >
              <ExternalLink className="size-3.5" />
              Open
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={() => handleCopy(xmlUrl, 'XML URL')}
            >
              {copied === 'XML URL' ? (
                <Check className="size-3.5 text-green-600" />
              ) : (
                <Link2 className="size-3.5" />
              )}
              Link
            </Button>
          </div>
        </div>
      </div>

      {/* Integrations (OData - tables and linked tables) */}
      {odataUrl && (
        <div>
          <label className="text-muted-foreground mb-1.5 block text-xs font-medium uppercase">
            Integrations
          </label>
          <div className="space-y-2">
            {/* OData row */}
            <div className="flex items-center gap-2">
              <div className="flex flex-1 items-center gap-2 text-sm">
                <Database className="size-4 text-purple-600" />
                <span>OData</span>
                <span className="text-muted-foreground text-xs">(Power BI / Excel)</span>
              </div>
              {publicKey && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7"
                  onClick={() => handleCopy(publicKey, 'API Key')}
                >
                  {copied === 'API Key' ? (
                    <Check className="size-3.5 text-green-600" />
                  ) : (
                    <KeyRound className="size-3.5" />
                  )}
                  Key
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-7"
                onClick={() => handleCopy(odataUrl, 'OData URL')}
              >
                {copied === 'OData URL' ? (
                  <Check className="size-3.5 text-green-600" />
                ) : (
                  <Link2 className="size-3.5" />
                )}
                Link
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// VERSION SELECTOR SECTION (for tables)
// ============================================================================

interface TableVersionSelectorProps {
  versions: VersionOption[];
  selectedVersion: string;
  onVersionChange: (version: string) => void;
}

export function TableVersionSelector({
  versions,
  selectedVersion,
  onVersionChange,
}: TableVersionSelectorProps) {
  return (
    <div>
      <label className="text-muted-foreground mb-1.5 block text-xs font-medium uppercase">
        Version
      </label>
      <VersionCombobox
        versions={versions}
        value={selectedVersion}
        onChange={onVersionChange}
        showSystemTagShortcuts
        showDate
      />
    </div>
  );
}

// ============================================================================
// LINKED TABLE VERSION SELECTORS
// ============================================================================

interface LinkedTableSource {
  id: string;
  alias: string;
  target_id: string;
  type: 'table' | 'linked_table';
}

interface SourceTableVersions {
  tableId: string;
  tableName: string;
  versions: VersionOption[];
}

interface LinkedTableVersionSelectorsProps {
  sources: LinkedTableSource[];
  sourceTableVersions: Record<string, SourceTableVersions>;
  versionSelections: Record<string, string>;
  onVersionChange: (alias: string, versionId: string) => void;
  isLoading?: boolean;
}

export function LinkedTableVersionSelectors({
  sources,
  sourceTableVersions,
  versionSelections,
  onVersionChange,
  isLoading,
}: LinkedTableVersionSelectorsProps) {
  if (sources.length === 0) return null;

  return (
    <div>
      <label className="text-muted-foreground mb-1.5 block text-xs font-medium uppercase">
        Table Versions
      </label>
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : (
        <div className="space-y-2">
          {sources
            .filter((source) => source.type === 'table')
            .map((source) => {
              const tableInfo = sourceTableVersions[source.alias];
              const versionOpts = tableInfo?.versions || [];
              const currentSelection = versionSelections[source.alias] || 'current';

              return (
                <div key={source.id} className="flex items-center gap-2">
                  <span
                    className="text-muted-foreground w-24 shrink-0 truncate text-sm"
                    title={tableInfo?.tableName || source.alias}
                  >
                    {tableInfo?.tableName || source.alias}
                  </span>
                  <VersionCombobox
                    versions={versionOpts}
                    value={currentSelection}
                    onChange={(value) => onVersionChange(source.alias, value)}
                    showSystemTagShortcuts
                    showDate
                    className="flex-1"
                  />
                </div>
              );
            })}
          {sources.some((s) => s.type === 'linked_table') && (
            <p className="text-muted-foreground text-xs italic">
              Linked table sources use their own version configuration
            </p>
          )}
        </div>
      )}
    </div>
  );
}
